'use client';

import { useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { Languages } from 'lucide-react';
import { Button, type buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { locales, localeNames, normalizeLocale, denormalizeLocale, type Locale } from '@/i18n/config';

type LanguageSwitcherProps = {
  variant?: VariantProps<typeof buttonVariants>['variant'];
  className?: string;
};

export function LanguageSwitcher({ variant = 'ghost', className }: LanguageSwitcherProps) {
  const pathname = usePathname();
  const params = useParams();

  // Get current locale from params
  const currentLocaleParam = params?.locale as string;
  const currentLocale = currentLocaleParam ? denormalizeLocale(currentLocaleParam) : 'pt-BR';

  /**
   * Generate the path for switching to a new locale
   * This preserves the current path but changes the locale segment
   */
  const getLocalePath = (newLocale: Locale): string => {
    if (!pathname) return '/';

    const segments = pathname.split('/').filter(Boolean);
    
    // Pattern: /[locale]/[storeId]/[...rest]
    if (segments.length >= 2) {
      const normalizedNewLocale = normalizeLocale(newLocale);
      const storeId = segments[1];
      
      // Replace the locale segment (first segment)
      const remainingPath = segments.slice(2).join('/');
      return `/${normalizedNewLocale}/${storeId}${remainingPath ? `/${remainingPath}` : ''}`;
    }

    return pathname;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="icon" className={cn(className)}>
          <Languages className="h-[1.2rem] w-[1.2rem]" />
          <span className="sr-only">Toggle language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {locales.map((locale) => (
          <DropdownMenuItem key={locale} asChild>
            <Link
              href={getLocalePath(locale)}
              className={`cursor-pointer ${
                locale === currentLocale ? 'font-bold bg-accent' : ''
              }`}
            >
              {localeNames[locale]}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
